import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Option, Select } from '../../aura';
import { FormContextData, useFormContext } from '../../state';
import {
  allTWCountryCodes,
  getCountryCodeFromTLD,
  getCountryCodeFromTimezone,
  getFlagEmoji,
} from '../../helpers';
import { useValidation } from './helpers/use-validation';

const regionNames = new Intl.DisplayNames([], {
  type: 'region',
});

const tzCountry = getCountryCodeFromTimezone();
const domainCountry = getCountryCodeFromTLD();
const options: Option<string>[] = allTWCountryCodes
  .sort((a, b) => {
    // Always put the timezone country first
    // Then the domain country
    // Then alphabetically
    if (a === tzCountry) {
      return -1;
    }
    if (b === tzCountry) {
      return 1;
    }
    if (a === domainCountry) {
      return -1;
    }
    if (b === domainCountry) {
      return 1;
    }
    return a.localeCompare(b);
  })
  .map(code => ({
    label: `${getFlagEmoji(code)}\t${regionNames.of(code) || code}`,
    value: code,
    key: code,
  }));

export function Country(): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'country' });
  const {
    data: {
      address: { countryCode },
    },
    setData,
  } = useFormContext();

  const country = useMemo(() => {
    return options.find(o => o.value === countryCode) || null;
  }, [countryCode]);

  const onChange = useCallback(
    (value: Option<string>) => {
      setData(current => ({
        ...current,
        address: {
          ...current.address,
          countryCode: value.value,
        },
      }));
    },
    [setData],
  );

  const hasValue = useCallback(
    (data: FormContextData) => (data.email ? null : 'Country is empty'),
    [],
  );
  const validate = useCallback((data: FormContextData) => {
    if (!data.address.countryCode) {
      return 'Country is empty';
    }
    if (!allTWCountryCodes.includes(data.address.countryCode)) {
      return 'Country is invalid';
    }
    return null;
  }, []);

  const { onBlur, subText, subTextFlavor } = useValidation(
    t,
    hasValue,
    validate,
  );

  return (
    <Select
      css={css({ '& .aura--select-options-section': { zIndex: 150 } })}
      options={options}
      id="country"
      label={t('label')}
      value={country}
      onChange={onChange}
      highlightFirstMatch
      filterOptionsFn={(input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase())
      }
      // @ts-expect-error explicitely put a wrong autocomplete type because browsers ignore the 'off' directive
      // https://caniuse.com/input-autocomplete-onoff
      autoComplete="treatwell"
      onBlur={onBlur}
      subText={subText}
      subTextFlavor={subTextFlavor}
    />
  );
}
